<template>
  <b-container
    :class="classes"
    class="d-flex align-items-center"
    fluid
    @click="linkClicked"
    :id="'tpc_' + id"
  >
    <b-row class="d-inline-flex topic-list-item-body">
      <!-- タイトル画像 -->
      <b-col
        class="d-inline-flex align-items-center px-2 topic-list-item-icon-container"
        style="max-width: 80px"
      >
        <profile-icon
          v-if="inSidemenu == false"
          class="topic-list-item-icon rounded-circle m-auto"
          :topicId="id"
          :userName="title"
          :image="iconUrl"
          :size="52"
        />
      </b-col>

      <b-col class="px-2 d-flex topic-list-item-container">
        <!-- カテゴリ、固定、 通知アイコン、 投稿数、☆数、投稿日時 -->
        <div class="d-inline-flex topic-header">
          <span :class="`badge badge-secondary category notselect-pointer ${categoryColor}`">{{
            categoryLabel
          }}</span>
          <div v-if="pinned" class="ml-2 notselect-pointer">
            <icon-pinned class="pinned"></icon-pinned>
          </div>
          <span
            v-if="!inSidemenu && notification"
            class="dicon dicon-speaker-on topic-notification"
          ></span>
          <div v-if="0 < messages" class="ml-2 d-inline-flex notselect-pointer messages-icon">
            <b-icon-chat-left class="mr-1"></b-icon-chat-left>{{ messages }}
          </div>
          <div v-if="0 < watches" class="ml-2 d-inline-flex notselect-pointer watches-icon">
            <b-icon-star class="mr-1"></b-icon-star>{{ watches }}
          </div>
          <span
            v-if="hasFeature('topic-guest-label') && allowedGuest && inSidemenu == false"
            class="ml-2 badge badge-secondary allow-guest notselect-pointer"
            >ゲスト可</span
          >

          <div class="pl-2 accessory notselect-pointer">{{ updatedAtLabel }}</div>
        </div>

        <!-- タイトル、ピン／お気に入り -->
        <div class="d-inline-flex topic-body">
          <div
            ref="topic-title"
            class="topic-title"
            v-html="normalizedTitleLabel"
            @click="onClick"
          ></div>
          <div v-if="starFlag && !search" class="accessory" v-on:click.stop.prevent="onStarClick">
            <b-iconstack class="star" font-scale="1"
              ><b-icon-star-fill class="has-star"></b-icon-star-fill
            ></b-iconstack>
          </div>
          <div v-else-if="!search" class="accessory" v-on:click.stop.prevent="onStarClick">
            <b-iconstack class="star" font-scale="1"
              ><b-icon-star-fill class="not-star"></b-icon-star-fill
            ></b-iconstack>
          </div>

          <!-- ツールチップ(タイトル省略時のみ表示) -->
          <b-tooltip
            @show="onTooltipShow"
            :target="() => $refs['topic-title']"
            :title="normalizedTitleLabel"
          />
        </div>
      </b-col>
    </b-row>
    <b-row class="topic-desc" ref="topic-desc">
      <div
        class="topic-title"
        v-html="$sanitize(desciption, { allowedAttributes: { span: ['class'] } })"
      ></div>
    </b-row>
  </b-container>
</template>

<script lang="ts">
import Vue, { computed, defineComponent, getCurrentInstance, ref, type PropType } from "vue";
import { useAppStore } from "@/store";
import "./topic-list-item.scss";
import IconPinned from "./icon/IconPinned.vue";
import ProfileIcon from "./ProfileIcon.vue";
import { useTextProcess } from "@/composables/useTextProcess";
import moment from "moment";
import { User } from "../model";
import type { Attachment, IconReaction } from "../model";
import { COLOR_PALETTE, DEFAULT_COLOR } from "./color-palette";
import sanitizeHTML from "../sanitize";
import { IconReactionType } from "../API";
import { ReactionController } from "../model/reaction-controller";
import S3AccessUtility from "./s3-acceess-utility";
import { allowFeature, FeatureName } from "../direct-app-config";
import "../icomoon/style.css";
Vue.prototype.$sanitize = sanitizeHTML;

export default defineComponent({
  name: "topic-list-item",
  components: {
    IconPinned,
    ProfileIcon,
  },
  props: {
    domainId: { type: String, default: () => "error" },
    id: { type: String, default: () => "", required: true },
    icon: { type: Object as PropType<Attachment>, default: () => undefined },
    category: { default: () => "カテゴリー", required: true },
    title: { type: String, default: () => "タイトル", required: true },
    desc: { type: String, default: () => "本文" },
    messages: { type: Number, default: () => 999 },
    updatedAt: { type: Date as PropType<Date>, default: () => new Date(), required: true },
    pinned: { type: Boolean, default: () => false },
    notification: { type: String, default: () => undefined },
    search: { type: String, default: () => "" },
    // true: サイドメニューの中
    inSidemenu: { type: Boolean, default: () => false },
    // ユーザー情報
    users: { type: Array as PropType<User[]>, default: () => [] }, // 組織内ユーザー情報
    viewer: { type: Object as PropType<User>, default: () => User.createNotFoundUser() }, // ログインユーザ情報
    acl: { required: true },
    // Reaction関係
    star: { type: Object as PropType<IconReaction>, default: () => undefined },
  },
  setup(props) {
    // 旧 TextProcessMixin → composable（1-D 利用側書き換え）
    const { highLightStr } = useTextProcess();
    // $refs / $router / $route は BootstrapVue / vue-router が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const selectionText = ref<string>("");

    const starFlag = computed(
      (): boolean => props.star?.userList.some((u) => u.userId == props.viewer.directId) || false
    );
    const watches = computed((): number => props.star?.userList.length || 0);

    const classes = computed((): { [selector: string]: boolean } => {
      return {
        "topic-list-item": true,
        search: !!props.search,
      };
    });

    const categoryLabel = computed((): string => {
      const title = props.category.title;
      if (!title) return "(指定無し)";
      if (window.innerWidth <= 320) {
        if (title.length > 8) return title.substr(0, 7) + "...";
      }
      if (window.innerWidth <= 420) {
        if (title.length > 12) return title.substr(0, 11) + "...";
      }
      return title || "(指定無し)";
    });

    const categoryColor = computed((): string => {
      // カテゴリーなし
      if (!props.category) return "";

      const color = props.category.color;
      const index = COLOR_PALETTE.findIndex((c) => c == color);
      return index < 0 ? DEFAULT_COLOR : color;
    });

    // タイトルラベル
    const titleLabel = computed((): string => highLightStr(props.title, props.search));

    // タイトルラベル（sanitize済み）
    const normalizedTitleLabel = computed((): string => {
      return sanitizeHTML(titleLabel.value, { allowedAttributes: { span: ["class"] } });
    });

    // 詳細
    const desciption = computed((): string => highLightStr(props.desc, props.search));

    // icon url
    const iconUrl = computed((): string => {
      return props.icon?.url || "";
    });

    const updatedAtLabel = computed((): string => {
      const dt = moment(props.updatedAt);
      if (moment().startOf("day") < dt) {
        return dt.format("HH:mm");
      } else if (moment().startOf("year") < dt) {
        return dt.format("MM/DD");
      } else {
        return dt.format("YYYY/MM/DD");
      }
    });

    /** true: ゲスト許可 */
    const allowedGuest = computed((): boolean => {
      if (!props.acl) return false;
      else return props.acl.guest.read;
    });

    const hasFeature = (keyword: FeatureName): boolean => {
      return allowFeature(keyword);
    };

    // ★クリック
    const onStarClick = (): void => {
      const ctl = new ReactionController();
      const topic = ctl.findTopic(props.id);
      if (!topic) return;
      ctl.onReactionClicked(topic, IconReactionType.FAVORITE, props.viewer, starFlag.value);
    };

    const onClick = (event: MouseEvent): void => {
      const selection = window.getSelection();
      const text = selection ? selection.toString() : "";
      if (0 < text.length) {
        // 文字選択
        event.preventDefault();
        event.stopPropagation();
        selectionText.value = text;
      } else if (0 < selectionText.value.length) {
        // 前回選択中だった -> Clearするだけ
        event.preventDefault();
        event.stopPropagation();
        selectionText.value = "";
      } else {
        // クリック処理(ここでは何もしない)
      }
    };

    // タイトル省略時のツールチップを出すかどうかを判定する
    const onTooltipShow = (bvEvent: Event): void => {
      const target = vm?.$refs["topic-title"];
      if (target instanceof HTMLElement) {
        // webkit-line-clamp で省略された時、offsetHeight < scrollHeigt になる
        const oHeight = target.offsetHeight;
        const sHeight = target.scrollHeight;
        if (oHeight < sHeight) return;
      }
      bvEvent.preventDefault(); // 出さない
    };

    const linkClicked = (): void => {
      if (vm?.$router && vm?.$route) {
        const path = `/${props.domainId}/${props.id}`;
        if (path == vm.$route.path) return; // 遷移先が同じ
        useAppStore().setDomainId(props.domainId);
        useAppStore().setTopicId(props.id);
        // s3-accesse-utility の topicId をセット
        const s3AccessUtility = S3AccessUtility.getInstance();
        s3AccessUtility.setTopicId(props.id);

        vm.$router.push(path);
      }
    };

    return {
      selectionText,
      starFlag,
      watches,
      classes,
      categoryLabel,
      categoryColor,
      titleLabel,
      normalizedTitleLabel,
      desciption,
      iconUrl,
      updatedAtLabel,
      allowedGuest,
      hasFeature,
      onStarClick,
      onClick,
      onTooltipShow,
      linkClicked,
    };
  },
});
</script>
